import { fetchWithAuth } from "../../utils/httpInterceptor";

const resolveBackendBaseUrl = () =>
  process.env.REACT_APP_LEGACY_BACKEND_URL ||
  process.env.REACT_APP_BUSINESS_BACKEND_URL ||
  process.env.REACT_APP_CORE_BACKEND_URL ||
  process.env.REACT_APP_LOAD_PAGE_URL ||
  "";

const toQueryString = (params) => {
  const query = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") {
      return;
    }
    if (Array.isArray(value)) {
      value
        .filter((item) => item !== undefined && item !== null && item !== "")
        .forEach((item) => query.append(key, String(item)));
      return;
    }
    query.set(key, String(value));
  });
  return query.toString();
};

const parseSseEventBlock = (eventBlock) => {
  const lines = eventBlock.split(/\r?\n/);
  let eventName = "message";
  const dataParts = [];
  lines.forEach((line) => {
    if (line.startsWith("event:")) {
      eventName = line.slice(6).trim();
      return;
    }
    if (line.startsWith("data:")) {
      dataParts.push(line.slice(5).trimStart());
    }
  });

  if (!dataParts.length) {
    return null;
  }

  const rawData = dataParts.join("\n");
  try {
    return {
      eventName,
      payload: JSON.parse(rawData),
    };
  } catch (error) {
    return null;
  }
};

const consumeSseStream = async (readableStream, onData, signal) => {
  const reader = readableStream.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";

  while (!signal.aborted) {
    const { value, done } = await reader.read();
    if (done) {
      break;
    }

    buffer += decoder.decode(value, { stream: true });
    const chunks = buffer.split(/\r?\n\r?\n/);
    buffer = chunks.pop() || "";

    chunks.forEach((chunk) => {
      const parsed = parseSseEventBlock(chunk);
      if (parsed) {
        onData(parsed);
      }
    });
  }
};

const reverseSyncExecutionService = {
  async listByContext({ entity, businessKey, businessKeyPrefix, dataAreaId, limit = 50 }) {
    const qs = toQueryString({ entity, businessKey, businessKeyPrefix, dataAreaId, limit });
    const response = await fetchWithAuth(`/api/reverse-sync/outbox?${qs}`, {
      baseUrl: resolveBackendBaseUrl(),
      method: "GET",
      headers: {
        ...(dataAreaId ? { "X-DataAreaId": dataAreaId } : {}),
      },
    });
    return response.json();
  },

  async getLatestByContext(context) {
    const rows = await this.listByContext({ ...context, limit: 1 });
    return Array.isArray(rows) && rows.length > 0 ? rows[0] : null;
  },

  subscribeByContext(context, handlers = {}) {
    const { onMessage, onError } = handlers;
    const abortController = new AbortController();
    let closed = false;

    const run = async () => {
      try {
        const qs = toQueryString(context);
        const response = await fetchWithAuth(`/api/reverse-sync/outbox/stream?${qs}`, {
          baseUrl: resolveBackendBaseUrl(),
          method: "GET",
          headers: {
            Accept: "text/event-stream",
            ...(context?.dataAreaId ? { "X-DataAreaId": context.dataAreaId } : {}),
          },
          signal: abortController.signal,
        });

        if (!response.body) {
          throw new Error("Reverse-sync stream unavailable.");
        }

        await consumeSseStream(
          response.body,
          (eventData) => onMessage && onMessage(eventData?.payload, eventData?.eventName || "message"),
          abortController.signal,
        );
      } catch (error) {
        if (closed || error?.name === "AbortError") {
          return;
        }
        if (onError) {
          onError(error instanceof Error ? error : new Error(String(error)));
        }
      }
    };

    run();

    return () => {
      closed = true;
      abortController.abort();
    };
  },
};

export default reverseSyncExecutionService;
